import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  BehaviorSubject,
  distinctUntilChanged,
  Observable,
  of,
  Subject,
  Subscription,
  debounceTime,
  take,
  withLatestFrom,
  combineLatest,
  filter,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { compareAsJson, unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import {
  Category,
  FinnairItineraryItemFlight,
  FinnairServiceItem,
  FinnairServiceVariantItemV3,
} from '@fcom/dapi/api/models';
import { getIconForServiceCategory } from '@fcom/dapi/utils';
import { IconButtonSize, IconButtonTheme } from '@fcom/ui-components';
import { SeatMapStatus, SeatMapWithStatus } from '@fcom/common/interfaces/seat-map.interface';

import {
  bundleServiceMatchesForSeatSelector,
  CommonBookingFeatureState,
  seatMapWithStatus,
} from '../../../../../store';
import { getInitials, getSeatInfoForTracking, getSeatMapFlight, servicesForFlightAndTravelerId } from '../../../utils';
import { SeatMapTraveler } from '../../../interfaces';

const DEFAULT_ICON_LIBRARY = IconLibrary.SVG_LIBRARY;

@Component({
  selector: 'fin-seat-map-renderer',
  styleUrls: ['./seat-map-renderer.component.scss'],
  templateUrl: './seat-map-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SeatMapRendererComponent implements AfterViewInit, OnDestroy, OnInit {
  private gtmService = inject(GtmService);
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);

  @Input() flight: FinnairItineraryItemFlight;
  @Input() activePaxId$: Observable<string>;
  @Input() enabledBundleCategory$: Observable<Category | undefined>;
  @Input() availableSeats: string[];
  @Input() passengers$: Observable<SeatMapTraveler[]>;
  @Input() selectedSeatsByPax$: Observable<Record<string, string>>;
  @Input() selectedBundleCategoriesByPax$: Observable<Record<string, Category[]>>;
  @Input() seatMapContainerWidth$: BehaviorSubject<number | undefined>;
  @Input() unavailableSeatLabel: string;
  @Input() includedSeats: FinnairServiceItem | null;
  @Input() seatSelectionDisabled$: Observable<boolean> = of(false);

  @Output() selectSeat: EventEmitter<{ passengerId: string; seatNumber: string; exitRow: boolean }> =
    new EventEmitter();
  @Output() updateScrollPosition: EventEmitter<number> = new EventEmitter();

  @ViewChild('seatMap') seatMap: ElementRef;
  @ViewChild('seatMapWrapper') seatMapWrapper: ElementRef;

  svgId: string;
  scrollPositionWatcher$ = new BehaviorSubject<'left' | 'center' | 'right'>('left');
  subscriptions: Subscription = new Subscription();
  templates: Record<string, () => SVGGElement> = {
    selected: () => this.seatMap.nativeElement.querySelector('.seat-selected-template')?.cloneNode(true),
    unavailable: () => this.seatMap.nativeElement.querySelector('.seat-unavailable-template')?.cloneNode(true),
  };
  isAtStart = true;
  isAtEnd = false;
  scrollPosition = 0;
  seatMapWithStatusForFlight$: Observable<SeatMapWithStatus | undefined>;

  scrollEvents$: Subject<number> = new Subject<number>();
  readonly scrollDebounceTime = 200;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconButtonSize = IconButtonSize;
  readonly SeatMapStatus = SeatMapStatus;

  ngOnInit(): void {
    this.seatMapWithStatusForFlight$ = this.store$.select(seatMapWithStatus(this.flight.id));
  }

  ngAfterViewInit(): void {
    this.setWrapperWidth();
    this.setSvgId();

    /* Whenever there's a change in passengers, we will mark seats with correct templates (unavailable, selected) for all passengers. */
    this.subscriptions.add(
      combineLatest([
        this.activePaxId$,
        this.passengers$,
        this.enabledBundleCategory$,
        this.selectedBundleCategoriesByPax$,
        this.selectedSeatsByPax$,
        this.store$.select(bundleServiceMatchesForSeatSelector),
      ])
        .pipe(
          filter(([_activePaxId, passengers]) => !!passengers?.length),
          distinctUntilChanged(compareAsJson),
          withLatestFrom(this.seatMapWithStatusForFlight$.pipe(filter((status) => !!status?.seatMap?.svg)))
        )
        .subscribe(
          ([
            [activePaxId, passengers, enabledBundleCategory, bundlesByPax, selectedSeatsByPax, bundleServiceBySeat],
            _seatMapWithStatus,
          ]) => {
            this.resetSeatView(passengers[0].id);
            passengers.forEach((passenger) => {
              if (passenger.id === activePaxId) {
                this.markSeatsUnavailableForPax(passenger.id);
                this.bringSelectedSeatTemplateOnTop(passenger.id);
                if (enabledBundleCategory) {
                  this.markSeatsWithoutBundlesUnavailable(
                    passenger.id,
                    enabledBundleCategory,
                    bundleServiceBySeat[this.flight.id]?.[activePaxId]?.[enabledBundleCategory] || {}
                  );
                }
              }

              if (selectedSeatsByPax[passenger.id]) {
                this.addSelectedSeatTemplateToPax(
                  passenger,
                  selectedSeatsByPax[passenger.id],
                  bundlesByPax[passenger.id] || []
                );
              }

              if (passenger.originalSeatNumber) {
                this.addOriginalSeatForPax(passenger);
              }
            });
          }
        )
    );

    this.subscriptions.add(
      this.scrollPositionWatcher$.pipe(distinctUntilChanged()).subscribe((position) => {
        this.enableScrollButtons(position);
      })
    );

    this.subscriptions.add(
      this.scrollEvents$.pipe(debounceTime(this.scrollDebounceTime)).subscribe((position) => {
        this.scrollPosition = position;
        this.updateScrollPosition.emit(position);
        this.gtmService.trackElement(
          'seatmap-full',
          GaContext.SEAT_MAP,
          ElementTypes.BUTTON,
          '',
          ElementActions.SCROLL
        );
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  addOriginalSeatForPax(passenger: SeatMapTraveler): void {
    const seatToSelect = this.seatMap.nativeElement.querySelector(`[data-id="${passenger.originalSeatNumber}"]`);
    seatToSelect.classList.add('current-seat');
  }

  markSeatsUnavailableForPax(paxId: string): void {
    this.seatMap.nativeElement.querySelectorAll(`[data-${paxId.toLowerCase()}="unavailable"]`).forEach((seat) => {
      const seatLink = seat.querySelector('a');
      const template = this.templates.unavailable();
      const originalAriaLabel = seatLink.getAttribute('aria-label');
      const seatNumber = seat.getAttribute('data-id');
      const occupiedSeat = seat.classList.contains('seat-occupied');

      seatLink.setAttribute('aria-label', `${seatNumber}, ${this.unavailableSeatLabel}`);
      seatLink.setAttribute('data-original-aria-label', originalAriaLabel);

      if (!occupiedSeat) {
        template.classList.remove('seat-unavailable-template');
        template.classList.add('seat-unavailable');
        seatLink.appendChild(template);
      }
    });
  }

  markSeatsWithoutBundlesUnavailable(
    paxId: string,
    enabledBundleCategory: Category | undefined,
    bundleServiceBySeat: Record<string, FinnairServiceVariantItemV3>
  ): void {
    this.seatMap.nativeElement.querySelectorAll(`[data-${paxId.toLowerCase()}]`).forEach((seat) => {
      const seatNumber = seat.getAttribute('data-id');

      if (!!enabledBundleCategory && !bundleServiceBySeat[seatNumber]) {
        const seatLink = seat.querySelector('a');
        const originalAriaLabel = seatLink.getAttribute('aria-label');

        seatLink.setAttribute('aria-label', `${seatNumber}, ${this.unavailableSeatLabel}`);
        seatLink.setAttribute('data-original-aria-label', originalAriaLabel);

        const template = this.templates.unavailable();
        template.classList.remove('seat-unavailable-template');
        template.classList.add('seat-unavailable');
        seatLink.appendChild(template);
      }
    });
  }

  addSelectedSeatTemplateToPax(passenger: SeatMapTraveler, selectedSeat: string, bundlesEnabled: Category[]): void {
    const initials = getInitials(passenger.traveler.firstName, passenger.traveler.lastName);
    const template = this.templates.selected();
    const seatToSelect = this.seatMap.nativeElement.querySelector(`[data-id="${selectedSeat}"] a`);

    const ariaLabel = seatToSelect.getAttribute('aria-label');
    seatToSelect.setAttribute(
      'aria-label',
      `${ariaLabel}. ${passenger.traveler.firstName} ${passenger.traveler.lastName}`
    );

    template.classList.remove('seat-selected-template');
    template.classList.add('seat-selected');
    template.querySelector('.pax-label').innerHTML = initials;

    const codeLabel = template.querySelector('.code-label');
    codeLabel.innerHTML = selectedSeat;
    if (bundlesEnabled.length > 0) {
      codeLabel.setAttribute('x', '18');
      bundlesEnabled.forEach((cat) => template.appendChild(this.createBundleIcon(cat)));
    }
    seatToSelect.appendChild(template);
  }

  bringSelectedSeatTemplateOnTop(paxId: string): void {
    /*  The seat-selected template is added before seat-unavailable template in the DOM.
    Thus it's not visible when changing to a pax with an infant, because we mark certain seats unavailable.
    We need to bring the selected seat template on top in the DOM. */
    this.seatMap?.nativeElement?.querySelectorAll(`[data-${paxId.toLowerCase()}="unavailable"]`).forEach((seat) => {
      const seatLink = seat.querySelector('a');
      const selectedSeat = seatLink?.querySelector('.seat-selected');

      if (selectedSeat) {
        seatLink.removeChild(selectedSeat); // first remove selectedSeat template from underneath unavailable seat template
        seatLink.appendChild(selectedSeat);
      }
    });
  }

  resetSeatView(paxId: string): void {
    const rootSelector = `[data-${paxId.toLowerCase()}]`;
    const selectedOrUnavailable = `${rootSelector} .seat-selected, ${rootSelector} .seat-unavailable`;

    this.seatMap.nativeElement.querySelectorAll(selectedOrUnavailable).forEach((node) => {
      if (node) {
        const originalAriaLabel = node.parentNode.getAttribute('data-original-aria-label');
        const ariaLabel = node.parentNode.getAttribute('aria-label');

        if (originalAriaLabel) {
          node.parentNode.setAttribute('aria-label', originalAriaLabel);
          node.parentNode.removeAttribute('data-original-aria-label');
        }

        node.parentNode.setAttribute('aria-label', ariaLabel?.split('.')[0]);
        node.parentNode.removeChild(node);
      }
    });
  }

  handleSeatClick(event: MouseEvent): void {
    event.preventDefault();
    const seatElement = (event.target as Element).closest('.seat');
    if (!seatElement) {
      return;
    }

    this.processSeatSelection(seatElement);
  }

  handleSeatKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();

    const seatElement = (event.target as Element).closest('.seat');
    if (!seatElement) {
      return;
    }

    this.processSeatSelection(seatElement);
  }

  private processSeatSelection(seatElement: Element): void {
    this.subscriptions.add(
      combineLatest([this.activePaxId$, this.enabledBundleCategory$, this.seatSelectionDisabled$])
        .pipe(
          take(1),
          withLatestFrom(this.selectedSeatsByPax$, this.store$.select(bundleServiceMatchesForSeatSelector))
        )
        .subscribe(([[activePaxId, bundleCategoryEnabled, seatSelectionDisabled], seatsByPax, bundleServiceBySeat]) => {
          if (seatSelectionDisabled) {
            return;
          }

          const isAvailableForPax = seatElement?.getAttribute(`data-${activePaxId.toLowerCase()}`) === 'available';
          const seatId = seatElement.getAttribute('data-id');
          const isExitRow = Boolean(seatElement.getAttribute('data-exitrow'));
          const paxWhoCurrentlyHasSeat = Object.entries(seatsByPax).find(([_pax, seat]) => seat === seatId)?.[0];
          // The passenger's own system-assigned (included) seat is not "available" for sale and is an
          // included service, yet the passenger must always be able to click back to restore it.
          const isOriginalSeatForPax = !!seatId && this.isIncludedForPassenger(activePaxId, seatId);

          if (!isAvailableForPax && seatId !== seatsByPax[activePaxId] && !isOriginalSeatForPax) {
            this.trackSeatSelection('seat-unavailable', seatElement, activePaxId);
            return;
          }

          if (isOriginalSeatForPax) {
            if (seatId !== seatsByPax[activePaxId]) {
              this.selectSeat.emit({ passengerId: activePaxId, seatNumber: seatId, exitRow: isExitRow });
              this.trackSeatSelection('seat-selected', seatElement, activePaxId);
            }
            return;
          }

          const bundlesByCategoryAndSeat = bundleServiceBySeat[this.flight.id]?.[activePaxId] as
            Partial<Record<Category, Record<string, FinnairServiceVariantItemV3>>> | undefined;
          if (
            bundleCategoryEnabled &&
            !bundlesByCategoryAndSeat?.[bundleCategoryEnabled]?.[seatId ?? ''] &&
            seatId !== seatsByPax[activePaxId]
          ) {
            return;
          }

          if (paxWhoCurrentlyHasSeat && paxWhoCurrentlyHasSeat !== activePaxId) {
            return;
          }

          if (!paxWhoCurrentlyHasSeat) {
            this.selectSeat.emit({
              passengerId: activePaxId,
              seatNumber: paxWhoCurrentlyHasSeat === undefined ? seatId : seatsByPax[activePaxId],
              exitRow: isExitRow,
            });
            this.trackSeatSelection('seat-selected', seatElement, activePaxId);
            return;
          }

          if (paxWhoCurrentlyHasSeat) {
            this.trackSeatSelection('seat-cleared', seatElement, activePaxId);
            this.selectSeat.emit({
              passengerId: paxWhoCurrentlyHasSeat,
              seatNumber: undefined,
              exitRow: false,
            });
          }
        })
    );
  }

  scrollSeatMap(direction: string): void {
    const seatMap = this.seatMapWrapper.nativeElement;
    const divWidth = seatMap.clientWidth / 2;

    if (direction === 'left') {
      this.scrollPosition = seatMap.scrollLeft + divWidth;
      seatMap.scrollBy({
        left: divWidth,
        behavior: 'smooth',
      });
    } else {
      this.scrollPosition = seatMap.scrollLeft - divWidth;
      seatMap.scrollBy({
        left: -divWidth,
        behavior: 'smooth',
      });
    }

    this.gtmService.seatMapEvent('seatmap-full', ElementTypes.BUTTON, 'arrow', ElementActions.CLICK, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  enableScrollButtons(event: 'left' | 'center' | 'right'): void {
    this.isAtStart = event === 'left';
    this.isAtEnd = event === 'right';
  }

  scrollTo(position: number): void {
    requestAnimationFrame(() => {
      const seatMap: HTMLDivElement = this.seatMapWrapper.nativeElement;

      seatMap.scrollTo({
        left: position,
        behavior: 'smooth',
      });
    });
  }

  handleScroll(): void {
    const seatMapElement = this.seatMapWrapper.nativeElement;
    const divWidth = seatMapElement.clientWidth;
    const seatMapWidth = seatMapElement.scrollWidth;
    const scrollLeft = Math.round(seatMapElement.scrollLeft); // Use rounding to get rid of half pixels
    this.scrollEvents$.next(scrollLeft);

    const isAtStart = scrollLeft === 0;
    const isAtEnd = scrollLeft === seatMapWidth - divWidth;
    const value = isAtStart ? 'left' : isAtEnd ? 'right' : 'center';

    this.scrollPositionWatcher$.next(value);
  }

  setWrapperWidth(): void {
    const svg = this.seatMap.nativeElement.querySelector('svg');
    const svgWidth = svg?.getAttribute('data-optimal-width');
    if (svgWidth) {
      this.seatMap.nativeElement.setAttribute('style', `width: ${svgWidth}px`);
    }
  }

  setSvgId(): void {
    this.svgId = `seatMapSvg-${this.flight.id}`;
    const svg = this.seatMap.nativeElement.querySelector('svg');
    if (svg && !svg.id) {
      svg.setAttribute('id', this.svgId);
    }
  }

  private trackSeatSelection(
    id: 'seat-selected' | 'seat-cleared' | 'seat-unavailable',
    seatElement: Element,
    activePaxId: string
  ): void {
    this.subscriptions.add(
      this.seatMapWithStatusForFlight$
        .pipe(
          filter((s) => !!s?.seatMap),
          take(1)
        )
        .subscribe((seatMap) => {
          this.gtmService.seatMapEvent(id, ElementTypes.BUTTON, 'cta', ElementActions.CLICK, {
            seatInfo: getSeatInfoForTracking(seatMap.seatMap, seatElement, activePaxId),
            flightInfo: getSeatMapFlight(this.flight),
          });
        })
    );
  }

  private isIncludedForPassenger(passengerId: string, seatId: string): boolean {
    return servicesForFlightAndTravelerId(this.includedSeats, this.flight.id, passengerId).some(
      ({ seatNumber }) => seatNumber === seatId
    );
  }

  private createBundleIcon(bundleCategory: Category): SVGElement {
    const bundleIcon = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    bundleIcon.setAttribute('href', `#${DEFAULT_ICON_LIBRARY}/${getIconForServiceCategory(bundleCategory)}`);
    bundleIcon.setAttribute('x', '29');
    bundleIcon.setAttribute('y', '24');
    bundleIcon.setAttribute('width', '16');
    bundleIcon.setAttribute('height', '16');
    bundleIcon.setAttribute('class', 'nordic-blue-900-text text-icon-primary');
    return bundleIcon;
  }
}
